"use client";

import { ViewType } from "@supabase/auth-ui-shared";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import AuthLegalNotice from "../../../components/AuthLegalNotice";
import AuthUI from "../../../components/AuthUI";
import Logo from "../../../components/Logo";
import { useSession } from "../../../components/SupabaseProvider";
import Typography from "../../../components/Typography";
import { AuthHelper } from "../../../helpers/auth_helper";
import { parseLoginRedirectParam } from "../../../helpers/signupRedirect";

interface AuthShellProps {
  // The "primary" form variant rendered on first paint. The two route-level
  // variants (`sign_in` on `/login`, `sign_up` on `/signup`) are routed to
  // their own URLs so each can be statically prerendered with its own SEO
  // metadata. The remaining sub-views (`forgotten_password`, `magic_link`)
  // stay as local state on whichever route the user is currently on.
  defaultView: "sign_in" | "sign_up";
}

const TITLES: Partial<Record<ViewType, string>> = {
  sign_in: "Welcome back",
  sign_up: "Create an account",
  forgotten_password: "Reset password",
  magic_link: "Sign in with magic link",
  update_password: "Update password",
};

// Shared shell for `/login` (sign-in) and `/signup` (sign-up). Splitting
// these into two distinct routes lets each be `force-static`-prerendered
// and gives them independent metadata, while still presenting a single
// composite UI to the user — clicking the "Don't have an account? Sign up"
// / "Already have an account? Sign in" toggle in `<AuthUI>` does a
// client-side `router.push` to the sibling route, preserving any
// `?redirect=` deep link in the query string.
export default function AuthShell({ defaultView }: AuthShellProps) {
  const session = useSession();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const redirectPath = parseLoginRedirectParam(searchParams?.get("redirect"));
  const [view, setView] = useState<ViewType>(defaultView);

  // `?clear=true` is the "Having trouble logging in?" escape hatch: nuke
  // any half-broken Supabase cookies and bounce to a clean `/login`.
  useEffect(() => {
    if (searchParams?.get("clear") === "true") {
      AuthHelper.removeAllCookies();
      toast.success("Cleared site data. Please try logging in again.");
      router.replace("/login");
    }
  }, [searchParams, router]);

  // Authenticated visitors don't belong on the auth pages — punt them to
  // wherever they were headed (`?redirect=`) or to their workspace.
  useEffect(() => {
    if (session) {
      router.replace(redirectPath);
    }
  }, [session, redirectPath, router]);

  // Bridges <AuthUI>'s in-form toggle to URL navigation for the two
  // route-level variants; sub-views (`forgotten_password`, `magic_link`)
  // remain local because they don't have their own pages.
  const handleViewChange = useCallback(
    (next: ViewType) => {
      const qs = searchParams?.toString() ?? "";
      const suffix = qs ? `?${qs}` : "";
      if (next === "sign_in" && pathname !== "/login") {
        router.push(`/login${suffix}`);
        return;
      }
      if (next === "sign_up" && pathname !== "/signup") {
        router.push(`/signup${suffix}`);
        return;
      }
      setView(next);
    },
    [pathname, router, searchParams],
  );

  if (session) return null;

  return (
    <div className="flex flex-col min-h-screen">
      <main className="max-w-sm mx-auto flex-1 flex-col flex items-center my-3 w-full h-full px-3">
        <div className="flex-1">
          <Link href="/">
            <Logo className="mb-4" />
          </Link>
        </div>
        <div className="w-full flex justify-center flex-col">
          <Typography
            variant="h3"
            component="h1"
            className="text-center mb-6"
          >
            {TITLES[view] ?? "Sign in"}
          </Typography>
          <AuthUI
            view={view}
            onViewChange={handleViewChange}
            redirectTo={
              (process.env.NEXT_PUBLIC_WEB_URL as string) + redirectPath
            }
            magicLink
          />
        </div>
        <div className="flex flex-col flex-1 items-center justify-center mt-4">
          <AuthLegalNotice />
          <Typography
            onClick={() => {
              AuthHelper.removeAllCookies();
              toast.success("Cleared site data. Please try again.");
            }}
            variant="overline"
            className="text-center mt-4 text-slate-400 cursor-pointer"
          >
            Having trouble logging in? Click here and try again.
          </Typography>
        </div>
      </main>
    </div>
  );
}
